import { useEffect, useRef, useState } from "react";
import {
  actions,
  desktop,
  setDesktopTelemetryContext,
  useAppStore,
} from "@/controller";
import { logClientEvent } from "@/telemetry";
import type {
  DesktopHostEvent,
  DesktopHostInfo,
  DesktopUpdateStatus,
} from "@/clients/desktop.client";
import type { ConnectedClient } from "@/types";
import {
  DESKTOP_AUTH_STORAGE_KEY,
  parseDesktopAuthReturn,
  type DesktopAuthRequest,
} from "@/desktop-auth";

const _logHostEvent = (event: DesktopHostEvent): void => {
  logClientEvent("runtime.desktopHostEvent", {
    status: event.event,
    ...(event.reason ? { reason: event.reason } : {}),
    ...(event.updaterStage ? { updaterStage: event.updaterStage } : {}),
    ...(event.updaterCode ? { updaterCode: event.updaterCode } : {}),
  });
};

const _acceptCheckoutReturn = (query: string): void => {
  const source = new URLSearchParams(
    query.startsWith("?") ? query.slice(1) : query,
  );
  const allowed = new URLSearchParams();
  for (const key of ["checkout_success", "plan", "session_id", "id"] as const) {
    const value = source.get(key);
    if (value) allowed.set(key, value);
  }
  if (!allowed.has("checkout_success")) return;
  window.location.assign(`/home?${allowed.toString()}`);
};

const _logUpdate = (status: DesktopUpdateStatus): void => {
  logClientEvent("runtime.desktopUpdate", { status: status.state });
};

const _acceptDesktopAuthReturn = async (query: string): Promise<void> => {
  const returned = parseDesktopAuthReturn(query);
  if (!returned) {
    logClientEvent("auth.handoffFailed", {
      method: "desktopBrowser",
      reason: "invalidReturn",
    });
    return;
  }
  let pending: DesktopAuthRequest | undefined;
  try {
    pending = JSON.parse(
      sessionStorage.getItem(DESKTOP_AUTH_STORAGE_KEY) || "null",
    ) as DesktopAuthRequest | undefined;
  } catch {
    logClientEvent("auth.handoffFailed", {
      method: "desktopBrowser",
      reason: "invalidRequest",
    });
    return;
  }
  if (!pending) {
    logClientEvent("auth.handoffFailed", {
      method: "desktopBrowser",
      reason: "missingRequest",
    });
    return;
  }
  if (returned.state !== pending.state) {
    logClientEvent("auth.handoffFailed", {
      method: "desktopBrowser",
      reason: "stateMismatch",
    });
    return;
  }
  try {
    const result = await actions.session.exchangeDesktopCode({
      code: returned.code,
      verifier: pending.verifier,
    });
    if ("error" in result) {
      logClientEvent("auth.handoffFailed", {
        method: "desktopBrowser",
        reason: "exchange",
      });
      return;
    }
    sessionStorage.removeItem(DESKTOP_AUTH_STORAGE_KEY);
    logClientEvent("auth.handoffCompleted", { method: "desktopBrowser" });
    window.location.assign("/home");
  } catch {
    logClientEvent("auth.handoffFailed", {
      method: "desktopBrowser",
      reason: "exchange",
    });
  }
};

const DesktopRuntimeBridge = () => {
  const userToken = useAppStore((state) => state.userToken);
  const connectedClients = useAppStore(
    (state) => state.userData?.connectedClients,
  );
  const [hostInfo, setHostInfo] = useState<DesktopHostInfo>();
  const registrationAttempt = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!desktop.isAvailable) return;
    let disposed = false;

    void desktop.ready().catch(() => undefined);
    void desktop
      .getInfo()
      .then((info) => {
        if (!disposed && info) {
          setHostInfo(info);
          setDesktopTelemetryContext({
            desktopOs: info.platform,
            desktopVersion: info.appVersion,
          });
          logClientEvent("runtime.desktopBridgeReady", {
            contractVersion: info.contractVersion,
            capabilityCount: info.capabilities.length,
          });
        }
      })
      .catch(() => undefined);
    void desktop
      .consumeHostEvents()
      .then((events) => {
        if (!disposed) events.forEach(_logHostEvent);
      })
      .catch(() => undefined);
    void desktop
      .consumeCheckoutReturns()
      .then((queries) => {
        const latest = queries[queries.length - 1];
        if (!disposed && latest) _acceptCheckoutReturn(latest);
      })
      .catch(() => undefined);
    void desktop
      .consumeDesktopAuthReturns()
      .then((queries) => {
        const latest = queries[queries.length - 1];
        if (!disposed && latest) void _acceptDesktopAuthReturn(latest);
      })
      .catch(() => undefined);

    // action-id: lifecycle.desktop.hostEvent
    const offHostEvent = desktop.onHostEvent(_logHostEvent);
    // action-id: lifecycle.desktop.checkoutReturn
    const offCheckout = desktop.onCheckoutReturn(_acceptCheckoutReturn);
    // action-id: lifecycle.desktop.authReturn
    const offDesktopAuth = desktop.onDesktopAuthReturn((query) => {
      void _acceptDesktopAuthReturn(query);
    });
    // action-id: lifecycle.desktop.updateStatus
    const offUpdate = desktop.onUpdateStatus(_logUpdate);
    return () => {
      disposed = true;
      offHostEvent();
      offCheckout();
      offDesktopAuth();
      offUpdate();
    };
  }, []);

  // action-id: lifecycle.desktop.connectionRegistered
  useEffect(() => {
    if (!userToken || !hostInfo) return;
    const connectedClient: ConnectedClient =
      hostInfo.platform === "macos" ? "macDesktop" : "windowsDesktop";
    if (connectedClients?.includes(connectedClient)) return;
    const attemptKey = `${userToken}:${connectedClient}`;
    if (registrationAttempt.current === attemptKey) return;
    registrationAttempt.current = attemptKey;
    void actions.user.update({
      connectedClients: [...(connectedClients || []), connectedClient],
    });
  }, [connectedClients, hostInfo, userToken]);

  return null;
};

export default DesktopRuntimeBridge;
